import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
  Button
} from '@mui/material';
import { imageAssetUrl } from '../../../../../../../utils/publicAssets';

const selectPackageImage = imageAssetUrl('choose_your_entries.png');

export type PackItem = {
  id: string;
  title: string;
  price: string;
  entries: number;
  nfts: number;
  description: string;
  dotColor: string; // orb color (can be linear-gradient)
};

type RaffleCarouselProps = {
  items: PackItem[];
  selectedId?: string; // ✅ allow "no default" by leaving this undefined
  onSelect?: (id: string) => void;
  prizeLabel?: string;
};

const RaffleCarousel = ({
  items,
  selectedId,
  onSelect,
  prizeLabel = 'A$10,000'
}: RaffleCarouselProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const scrollerRef = useRef<HTMLDivElement>(null);

  // ✅ if nothing selected, don’t auto-select index 0
  const selectedIndex = useMemo(() => {
    if (!selectedId) return -1;
    return items.findIndex((x) => x.id === selectedId);
  }, [items, selectedId]);

  // dots active index (mobile)
  const [activeIndex, setActiveIndex] = useState(0);

  // keep active index in sync with selected on mobile (only if selected exists)
  useEffect(() => {
    if (!isMobile) return;
    if (selectedIndex < 0) return;

    setActiveIndex(selectedIndex);

    requestAnimationFrame(() => {
      const el = scrollerRef.current;
      if (!el) return;

      const child = el.children[selectedIndex] as HTMLElement | undefined;
      child?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    });
  }, [isMobile, selectedIndex]);

  const VALUE_PER_UNIT = 3; // $3 per NFT/Entry
  const BONUS_CAP = 40; // inferred from your screenshot (cap)
  const SAVE_ROUND_TO = 1; // rounds to nearest $1 for the pill ("Save $70")

  const roundToNearest = (n: number, step: number) =>
    Math.round(n / step) * step;

  const calcValueBanner = (pack: PackItem) => {
    const units = Number(pack.nfts || 0); // or pack.entries if you prefer
    const price = Number(pack.price || 0);

    const baseValue = units * VALUE_PER_UNIT;

    // Bonus logic: grows with units but caps (matches your screenshot behavior)
    // If you want “no bonus” just set BONUS_CAP = 0
    const bonusValue = Math.min(
      BONUS_CAP,
      Math.max(0, units * VALUE_PER_UNIT - 20)
    );
    // ^ you can change this rule later; cap is the main thing

    const totalValue = baseValue + bonusValue;
    const save = Math.max(0, totalValue - price);

    return {
      totalValue: Math.round(totalValue), // screenshot shows whole dollars
      saveLabel: `Save A$${roundToNearest(save, SAVE_ROUND_TO).toFixed(0)}`
    };
  };

  const minUnits = useMemo(() => {
    if (!items.length) return 0;
    return Math.min(...items.map((p) => Number(p.nfts || 0)));
  }, [items]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;

    const children = Array.from(el.children) as HTMLElement[];
    if (!children.length) return;

    const center = el.scrollLeft + el.clientWidth / 2;
    let bestIdx = 0;
    let bestDist = Infinity;

    children.forEach((c, i) => {
      const cCenter = c.offsetLeft + c.clientWidth / 2;
      const dist = Math.abs(center - cCenter);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    });

    setActiveIndex(bestIdx);
  };

  const handleSelect = (id: string) => onSelect?.(id);

  const clamp = (n: number, min: number, max: number) =>
    Math.min(max, Math.max(min, n));

  const alphaHex = (hex: string, a: number) => {
    // hex like #FF4AD8, a 0..1
    const x = hex.replace('#', '');
    const full =
      x.length === 3
        ? x
            .split('')
            .map((c) => c + c)
            .join('')
        : x;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${clamp(a, 0, 1)})`;
  };

  /* ---------------- styles ---------------- */

  const titleSx = {
    color: 'rgba(240,255,255,0.95)',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    lineHeight: 1.05,
    textAlign: 'center'
  };

  // ✅ Fix wide-screen alignment: center it AND center the block itself
  const subtitleSx = {
    mt: 1.2,
    color: 'rgba(240,255,255,0.55)',
    fontWeight: 500,
    fontSize: { xs: 18, sm: 20, md: 22 },
    maxWidth: 820,
    textAlign: 'center',
    mx: 'auto' // ✅ this is the missing bit (centers the maxWidth block)
  };

  const cardSx = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 5,
    p: { xs: 2.6, md: 3 },

    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: { xs: 340, sm: 380, md: 450, lg: 430, xl: 410 },

    background:
      'linear-gradient(180deg, rgba(14,22,24,0.78), rgba(9,14,15,0.62))',
    border: '1px solid rgba(255,255,255,0.08)',
    // boxShadow: '0 22px 65px rgba(0,0,0,0.45)',
    backdropFilter: 'blur(12px)',
    transition:
      'transform .22s ease, box-shadow .22s ease, border-color .22s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      // boxShadow: '0 28px 85px rgba(0,0,0,0.55)',
      borderColor: 'rgba(255,255,255,0.14)'
    }
  };

  const selectedCardSx = (solid: string) => ({
    borderColor: alphaHex(solid, 0.45)
    // boxShadow: `0 30px 95px rgba(0,0,0,0.62), 0 0 0 1px ${alphaHex(
    //   solid,
    //   0.18
    // )} inset`
  });

  const orbSx = {
    width: 38,
    height: 38,
    borderRadius: '50%'
    // boxShadow:
    //   '0 8px 18px rgba(0,0,0,0.32), inset 0 10px 18px rgba(255,255,255,0.22)',
    // filter: 'saturate(1.1)'
  };

  const packTitleSx = {
    mt: 1.2,
    color: 'rgba(240,255,255,0.95)',
    fontWeight: 800,
    fontSize: { xs: 34, sm: 36, md: 28, lg: 26, xl: 24 },
    letterSpacing: '-0.02em'
  };

  const priceSx = {
    mt: 0.15,
    color: 'rgba(240,255,255,0.95)',
    fontWeight: 900,
    fontSize: { xs: 34, sm: 36, md: 28, lg: 26, xl: 24 },
    letterSpacing: '-0.02em'
  };

  const featureTextSx = {
    color: 'rgba(240,255,255,0.92)',
    fontWeight: 700,
    fontSize: { xs: 18, md: 14, xl: 13 }
  };

  const descSx = {
    color: 'rgba(240,255,255,0.42)',
    fontWeight: 600,
    fontSize: { xs: 16, md: 13.5, xl: 13 },
    lineHeight: 1.35
  };

  /* ---------------- sub components ---------------- */

  const FeatureRow = ({
    icon,
    label
  }: {
    icon: React.ReactNode;
    label: string;
  }) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2.2,
            padding: 0.5,
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <Box sx={{ color: 'rgba(240,255,255,0.92)' }}>{icon}</Box>
        </Box>

        <Typography
          sx={{
            color: 'rgba(240,255,255,0.92)',
            fontWeight: 750,
            fontSize: { xs: 18, md: 14, xl: 13 }
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  const getPackSavings = (pack: PackItem) => {
    const units = Number(pack.nfts || 0);
    const price = Number(pack.price || 0);

    const baseValue = units * VALUE_PER_UNIT;

    const bonusValue = Math.min(
      BONUS_CAP,
      Math.max(0, units * VALUE_PER_UNIT - 20)
    );

    const totalValue = baseValue + bonusValue;
    return Math.max(0, totalValue - price); // "save"
  };

  const topBadgesById = useMemo(() => {
    // Sort by "save" desc. Tie-breaker: higher units, then cheaper.
    const ranked = [...items]
      .map((p) => ({
        id: p.id,
        save: getPackSavings(p),
        units: Number(p.nfts || 0),
        price: Number(p.price || 0)
      }))
      .sort((a, b) => {
        if (b.save !== a.save) return b.save - a.save;
        if (b.units !== a.units) return b.units - a.units;
        return a.price - b.price;
      });

    const best = ranked[0]?.id ?? null;
    const popular = ranked[1]?.id ?? null;

    return { best, popular };
  }, [items]);

  const PackCard = ({
    item,
    selected,
    onClick
  }: {
    item: PackItem;
    selected?: boolean;
    onClick?: () => void;
  }) => {
    const solid = getSolidFromDotColor(item.dotColor);

    const units = Number(item.nfts || 0);
    const showBanner = units > minUnits; // keeps smallest pack clean
    const banner = showBanner ? calcValueBanner(item) : null;

    const isBest = item.id === topBadgesById.best;
    const isPopular = item.id === topBadgesById.popular;
    const isFeatured = isBest || isPopular;

    // subtle gradient border trick (no tacky neon)
    const borderGradient = isFeatured
      ? `linear-gradient(135deg, ${alphaHex(solid, 0.95)}, ${alphaHex(
          solid,
          0.25
        )} 55%, rgba(255,255,255,0.12))`
      : `linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04))`;

    const innerGlass = `linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))`;

    const titleName = item.title.split(' Pack')[0];

    return (
      <Box
        onClick={onClick}
        role="button"
        tabIndex={0}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: { xs: 4, md: 6 },
          overflow: 'visible',
          p: '2px',
          backgroundImage: borderGradient,
          boxShadow: 'none',
          transform: 'none',
          transition: 'transform .2s ease, box-shadow .2s ease',
          '&:hover': {
            transform: 'translateY(-8px) scale(1.02)',
            boxShadow: 'none'
          }
        }}
      >
        {isBest && <TopRibbon text="Best Value" tone="purple" />}
        {isPopular && <TopRibbon text="Most Popular" tone="green" />}

        <Box
          sx={{
            borderRadius: { xs: 4, md: 6 },
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: item.dotColor,
            backgroundImage: item.dotColor,

            display: 'grid',
            gridTemplateRows: 'auto 1fr auto',
            minHeight: { xs: 290, sm: 325, md: 390, lg: 370, xl: 360 },
            py: { xs: 1.7, sm: 1.9, md: 2.4, lg: 2.1 },
            px: { xs: 1.35, md: 1.8, lg: 1.35 },
            boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.08)'
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              pt: isFeatured
                ? { xs: 4.1, sm: 4.5, md: 5.2, lg: 5.1 }
                : { xs: 3.9, sm: 4.3, md: 5.2, lg: 5.4 }
            }}
          >
            <Typography
              sx={{
                color: '#ffffff',
                fontWeight: 900,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                fontSize: { xs: 18, sm: 25, md: 30, lg: 24, xl: 26 },
                lineHeight: 0.95,
                mt: -0.5,
                textShadow: '0 3px 12px rgba(0,0,0,0.18)'
              }}
              textAlign="center"
            >
              {titleName}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.92)',
                fontWeight: 900,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: { xs: 10, sm: 12, md: 13, lg: 11.5, xl: 12 },
                mt: 0.35,
                textShadow: '0 2px 10px rgba(0,0,0,0.18)'
              }}
              textAlign="center"
            >
              One-off Pack
            </Typography>
          </Box>

          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box
              sx={{
                borderRadius: 4,
                px: { xs: 1.1, md: 1.2, lg: 0.8 },
                py: { xs: 0.65, sm: 0.8, md: 1.1, lg: 0.8 }
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gap: 0.85
                }}
              >
                <Typography
                  fontWeight={900}
                  textAlign="center"
                  sx={{
                    fontSize: { xs: 46, sm: 58, md: 72, lg: 58, xl: 62 },
                    lineHeight: 1,
                    color: '#ffffff',
                    textShadow: '0 8px 18px rgba(0,0,0,0.14)'
                  }}
                >
                  {item.entries}
                </Typography>
                <Box>
                  <Typography
                    textAlign="center"
                    fontWeight={800}
                    sx={{
                      color: '#ffffff',
                      fontSize: {
                        xs: 14.5,
                        sm: 16.5,
                        md: 19,
                        lg: 15.5,
                        xl: 16.5
                      },
                      letterSpacing: '0.02em',
                      lineHeight: 1.08,
                      textShadow: '0 2px 10px rgba(0,0,0,0.14)'
                    }}
                  >{`${item.entries === 1 ? 'ENTRY INTO' : 'ENTRIES INTO'}`}</Typography>

                  <Typography
                    textAlign="center"
                    fontWeight={800}
                    sx={{
                      color: '#ffffff',
                      fontSize: {
                        xs: 14.5,
                        sm: 16.5,
                        md: 19,
                        lg: 15.5,
                        xl: 16.5
                      },
                      letterSpacing: '0.02em',
                      lineHeight: 1.08,
                      textShadow: '0 2px 10px rgba(0,0,0,0.14)'
                    }}
                  >
                    GIVEAWAY
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mt: { xs: 0.75, sm: 0.85, md: 1 }
                    }}
                  >
                    <Box
                      sx={{
                        px: { xs: 0.8, sm: 1, md: 1.1, lg: 0.85 },
                        py: 0.4,
                        borderRadius: 999,
                        background: 'rgba(0,0,0,0.24)',
                        color: '#ffffff',
                        fontWeight: 900,
                        fontSize: {
                          xs: 10.8,
                          sm: 12.4,
                          md: 13.5,
                          lg: 11.5,
                          xl: 12.5
                        },
                        lineHeight: 1.15,
                        textAlign: 'center',
                        maxWidth: '100%',
                        boxShadow:
                          'inset 0 1px 0 rgba(255,255,255,0.16), 0 4px 10px rgba(0,0,0,0.12)'
                      }}
                    >
                      + {item.nfts} EXCLUSIVE{' '}
                      {item.nfts === 1
                        ? 'DIGITAL COLLECTIBLE'
                        : 'DIGITAL COLLECTIBLES'}
                    </Box>
                  </Box>
                  <Box sx={{ my: { xs: 0.25, sm: 0.4, md: 0.6 } }} />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              pt: { xs: 0.45, sm: 0.65, md: 1 },
              backgroundColor: 'transparent',
              background: 'transparent',
              backgroundImage: 'none',
              boxShadow: 'none',
              border: 'none'
            }}
          >
            <Divider
              sx={{
                mt: { xs: -0.8, lg: -0.6 },
                mb: { xs: 1.4, lg: 1.2 },
                borderColor: 'rgba(255,255,255,0.2)'
              }}
            />

            <Box
              sx={{
                minHeight: { xs: 62, sm: 68, md: 76, lg: 66 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                my: -0.5
              }}
            >
              {banner ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      mt: -2
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.88)',
                        fontWeight: 800,
                        fontSize: {
                          xs: 12.5,
                          sm: 13,
                          md: 14.5,
                          lg: 12.5,
                          xl: 13.5
                        }
                      }}
                    >
                      Total Value
                    </Typography>

                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.75)',
                        fontWeight: 900,
                        textDecoration: 'line-through',
                        textDecorationThickness: '2px',
                        fontSize: { xs: 14, sm: 15, md: 17, lg: 14.5, xl: 15.5 }
                      }}
                    >
                      ${banner.totalValue}
                    </Typography>
                  </Box>

                  {/* <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'white',
                        fontWeight: 900,
                        textDecorationThickness: '2px'
                      }}
                    >
                      {banner.saveLabel}
                    </Typography>
                  </Box> */}

                  <Box
                    sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}
                  >
                    <Box
                      sx={{
                        px: { xs: 1.8, lg: 1.2 },
                        py: { xs: 0.65, lg: 0.45 },
                        borderRadius: 999,
                        border: '1px solid rgba(255,255,255,0.24)',
                        background: 'rgba(255,255,255,0.18)',
                        color: '#ffffff',
                        fontWeight: 950,
                        letterSpacing: '-0.01em',
                        backdropFilter: 'blur(6px)',
                        fontSize: { xs: 12, sm: 12.5, md: 14, lg: 11.5 }
                      }}
                    >
                      {banner.saveLabel}
                    </Box>
                  </Box>
                </>
              ) : (
                <Box sx={{ height: 1 }} />
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                mt: { xs: -1.6, sm: -1.8, lg: -1.6 }
              }}
            >
              <Typography
                sx={{
                  fontWeight: 950,
                  fontSize: { xs: 34, sm: 40, md: 48, lg: 38, xl: 40 },
                  letterSpacing: '-0.03em',
                  color: '#ffffff',
                  textShadow: '0 8px 18px rgba(0,0,0,0.18)'
                }}
              >
                ${item.price}
              </Typography>
            </Box>

            <Typography
              color="rgba(255,255,255,0.82)"
              fontWeight={800}
              textAlign="center"
              sx={{
                mt: -0.4,
                fontSize: { xs: 10, sm: 11, md: 12.5, lg: 10.5 }
              }}
            >
              (Single Payment Only)
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: { xs: 1.25, sm: 1.45, lg: 1.3 }
              }}
            >
              <Button
                sx={{
                  fontSize: { xs: 14, sm: 15, md: 18, lg: 15 },
                  fontWeight: 900,
                  backgroundColor: selected
                    ? 'rgba(255,255,255,0.26)'
                    : '#ffffff',
                  color: '#0f172a',
                  border: `1px solid ${selected ? 'rgba(255,255,255,0.42)' : 'rgba(255,255,255,0.42)'}`,
                  borderRadius: 2,
                  px: { xs: 3.2, md: 3.8, lg: 2.8 },
                  py: { xs: 0.7, sm: 0.8, lg: 0.75 },
                  minWidth: { xs: 108, sm: 118, md: 150, lg: 124 },
                  boxShadow:
                    '0 10px 20px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.65)',
                  '&:hover': {
                    backgroundColor: selected
                      ? 'rgba(255,255,255,0.28)'
                      : '#f8fafc'
                  }
                }}
                variant="outlined"
              >
                {selected ? 'SELECTED' : 'SELECT'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const Dots = ({ count, active }: { count: number; active: number }) => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1.6,
          mt: 1
        }}
      >
        {Array.from({ length: count }).map((_, i) => {
          const isActive = i === active;
          return (
            <Box
              key={i}
              sx={{
                width: isActive ? 64 : 14,
                height: 14,
                borderRadius: 999,
                transition:
                  'width 0.25s ease, opacity 0.25s ease, filter 0.25s ease',
                background: isActive
                  ? 'rgba(124,255,250,0.95)'
                  : 'rgba(255,255,255,0.25)',
                opacity: isActive ? 1 : 0.7,
                filter: isActive
                  ? 'drop-shadow(0 0 12px rgba(124,255,250,0.65))'
                  : 'none'
              }}
            />
          );
        })}
      </Box>
    );
  };

  const getSolidFromDotColor = (dotColor: string) => {
    // If it's a gradient, grab the first hex color stop (e.g. #FF4AD8)
    const m = dotColor.match(/#(?:[0-9a-fA-F]{3}){1,2}/);
    return m?.[0] ?? '#7CFFFA'; // fallback
  };

  const CARD_RADIUS = 20; // or 22/24 — just match your inner card radius

  const getRibbonStyles = (tone: 'green' | 'purple') => {
    if (tone === 'green') {
      return {
        background: '#16A34A',
        color: '#FFFFFF'
      };
    }

    return {
      background: '#C026D3',
      color: '#FFFFFF'
    };
  };

  const TopRibbon = ({
    text,
    tone
  }: {
    text: string;
    tone: 'green' | 'purple';
  }) => {
    const ribbonStyles = getRibbonStyles(tone);
    const ribbonShadow = 'rgba(15,23,42,0.22)';

    return (
      <Box
        sx={{
          position: 'absolute',
          top: { xs: -8, md: -10, lg: -10 },
          left: '50%',
          width: { xs: '94%', sm: '90%', md: '86%' },
          height: { xs: 36, sm: 40, md: 46, lg: 44 },
          transform: 'translateX(-50%) rotate(-1deg) scale(1)',
          transformOrigin: 'center center',
          borderRadius: 999,
          overflow: 'hidden',
          zIndex: 4,
          background: ribbonStyles.background,
          border: '3px solid #ffffff',
          boxSizing: 'border-box',
          boxShadow: `0 14px 22px ${ribbonShadow}, 0 6px 0 rgba(15,23,42,0.18), inset 0 2px 0 rgba(255,255,255,0.5), inset 0 -8px 16px rgba(0,0,0,0.16)`,
          animation: 'featuredRibbonPulse 2.2s ease-in-out infinite',
          '@keyframes featuredRibbonPulse': {
            '0%, 100%': {
              transform: 'translateX(-50%) rotate(-1deg) scale(1)',
              boxShadow: `0 14px 22px ${ribbonShadow}, 0 6px 0 rgba(15,23,42,0.18), inset 0 2px 0 rgba(255,255,255,0.5), inset 0 -8px 16px rgba(0,0,0,0.16)`
            },
            '50%': {
              transform: 'translateX(-50%) rotate(-1deg) scale(1.075)',
              boxShadow: `0 14px 22px ${ribbonShadow}, 0 6px 0 rgba(15,23,42,0.18), inset 0 2px 0 rgba(255,255,255,0.5), inset 0 -8px 16px rgba(0,0,0,0.16)`
            }
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 14,
            top: '50%',
            width: 7,
            height: 7,
            borderRadius: 999,
            transform: 'translateY(-50%)',
            background: '#ffffff',
            boxShadow: '0 0 10px rgba(255,255,255,0.72)'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 14,
            top: '50%',
            width: 7,
            height: 7,
            borderRadius: 999,
            transform: 'translateY(-50%)',
            background: '#ffffff',
            boxShadow: '0 0 10px rgba(255,255,255,0.72)'
          }}
        />
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            sx={{
              color: ribbonStyles.color,
              fontWeight: 1000,
              fontSize: { xs: 10.5, sm: 12, md: 14, lg: 13.5 },
              letterSpacing: { xs: '0.08em', sm: '0.12em', md: '0.16em' },
              textTransform: 'uppercase',
              textShadow: '0 2px 8px rgba(0,0,0,0.22)',
              WebkitTextStroke: '0.35px currentColor'
            }}
          >
            {text}
          </Typography>
        </Box>
      </Box>
    );
  };

  /* ---------------- render ---------------- */

  return (
    <Box
      sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          component="img"
          src={selectPackageImage}
          alt="Select a package"
          sx={{
            width: '100%',
            maxWidth: { xs: 640, md: 860, lg: 980 },
            height: 'auto',
            mt: { xs: -1, md: -2 }
          }}
        />
      </Box>

      <Typography
        color="black"
        textAlign="center"
        variant="h5"
        fontWeight={600}
        sx={{ mb: 4 }}
      >
        Start your journey with INCRYPT by purchasing a one-off pack to be
        entered into this month&apos;s {prizeLabel} giveaway.
      </Typography>

      {/* <Typography
        sx={{
          ...subtitleSx,
          maxWidth: 920,
          color: '#334155',
          mt: { xs: -2.5, md: -3 },
          fontSize: { xs: 16, sm: 18, md: 20 },
          lineHeight: 1.5,
          px: { xs: 1, md: 0 }
        }}
      >
        By purchasing any INCRYPT Package below, your name will instantly go
        into the {prizeLabel} INC Giveaway
      </Typography> */}

      <Box
        sx={{
          // mt: 5,
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))'
          },
          columnGap: { xs: 0.8, sm: 1, md: 2, lg: 2 },
          rowGap: { xs: 3.5, md: 4, lg: 3.5 },
          alignItems: 'stretch',
          gridAutoRows: '1fr',
          width: '100%',
          maxWidth: { md: 1120, lg: 1120, xl: 1180 },
          mx: 'auto',
          px: { xs: 0, sm: 0.5, md: 0 },
          '& > :last-child:nth-child(odd)': {
            gridColumn: { xs: '1 / -1', md: '1 / -1', lg: 'auto' },
            justifySelf: { xs: 'center', lg: 'stretch' },
            width: {
              xs: 'calc((100% - 6.4px) / 2)',
              sm: 'calc((100% - 8px) / 2)',
              md: 'calc((100% - 48px) / 4)',
              lg: '100%'
            }
          }
        }}
      >
        {items.map((item) => (
          <PackCard
            key={item.id}
            item={item}
            selected={item.id === selectedId}
            onClick={() => handleSelect(item.id)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default RaffleCarousel;
